import AuthService from '@apis/AuthService';
import WorkspaceService from '@apis/WorkspaceService';
import { COGNITO_LOGOUT_URL } from '@config/config';
import { MasqueradeUser } from '@customTypes/auth/Auth';
import LocalStorageKey from '@enums/LocalStorageKey';
import { UserRole, UserType } from '@enums/Permissions';
import { useEffectOnce } from '@hooks/useEffectOnce';
import * as Sentry from '@sentry/react';
import { setChatContext } from '../libs/sentryChat';
import LocalStorageService from '@services/LocalStorageService';
import { useAppStore } from '@stores/app/appStore';
import { getCognitoLogoutUrl } from '@utils/federatedAuth';
import { IS_ANONYMOUS_APP, LANDING_PAGE_URL } from '@config/config';
import { useAppSelectors } from '@stores/app/appStore';
import FullscreenLoader from '@components/FullscreenLoader';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { queryClient } from '../libs/queryClient';
import posthog, { aliasLandingSession } from '@libs/posthog';
import { EVENTS } from '@lib/analytics';

const REFRESH_CHANNEL_NAME = 'leonardo_auth_refresh';
type RefreshMessage = { type: 'refresh_start' | 'refresh_done'; tabId: string; timestamp: number };
const TAB_ID = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const PEER_REFRESH_STALE_MS = 15_000;

interface SetUserLoginType {
  profile?: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  accessTokenExpires: number;
  isAdmin: boolean;
  isAnonymous: boolean;
  setUserlogin: (data?: SetUserLoginType) => void;
  hasRole: (role: UserRole) => boolean;
  logout: () => void;
  currentUser: any;
  loadUserProfile: (
    masquadeUser?: MasqueradeUser | null,
    isSilent?: boolean,
  ) => void;
  updateOnboardStep: (step: number) => void;
  setMasqueradeLogin: (data: MasqueradeUser | null) => void;
  masqueradeUser: MasqueradeUser | null;
  isSuperAdmin: boolean;
  isMasquerading: boolean;
  isInternalUser: boolean;
  isFreeTrialExpired: boolean;
  isBrandingEnabled: boolean;
}

// Create context with explicit null default
const AuthContext = createContext<AuthContextType | null>(null);
const storage = LocalStorageService;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const demoMode = useAppSelectors.demoMode();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [accessTokenExpires, setAccessTokenExpires] = useState<number>(() => {
    try {
      return storage.getItem<number>(LocalStorageKey.ACCESS_TOKEN_EXPIRES) || 0;
    } catch (error) {
      console.warn('Failed to get access token expires from storage:', error);
      return 0;
    }
  });
  const [masqueradeUser, setMasqueradeUser] = useState<MasqueradeUser | null>(
    () => {
      try {
        return storage.getItem<MasqueradeUser>(LocalStorageKey.MASQUERADE_USER) || null;
      } catch (error) {
        return null;
      }
    },
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [isMasquerading, setIsMasquerading] = useState<boolean>(false);
  const [isSilent, setIsSilent] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      return !!storage.getItem(LocalStorageKey.USER_PROFILE);
    } catch (error) {
      console.warn('Failed to check authentication state:', error);
      return false;
    }
  });
  // Use ref to track authentication state for timeout callbacks
  const isAuthenticatedRef = useRef(isAuthenticated);
  const isRefreshingRef = useRef(false);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  
  const setCompanyName = useAppStore((state) => state.setCompanyName);
  const clearAllTabs = useAppStore((state) => state.clearAllTabs);
  const setDemoMode = useAppStore((state) => state.setDemoMode);

  const isAdmin = useMemo(() => {
    return (
      currentUser?.user_type !== UserRole.STAFF ||
      currentUser?.user_type === UserRole.ADMIN
    );
  }, [currentUser]);

  const isBrandingEnabled = useMemo(() => {
    return currentUser?.is_branding_enabled;
  }, [currentUser]);

  const isAnonymous = useMemo(() => {
    return currentUser?.user_type === UserType.ANONYMOUS;
  }, [currentUser]);

  const isSuperAdmin = useMemo(() => {
    return currentUser?.is_support;
  }, [currentUser]);

  const isFreeTrialExpired = useMemo(() => {
    return currentUser?.is_subscription_expired;
  }, [currentUser]);

  const isInternalUser = useMemo(() => {
    const email = currentUser?.user_name ?? '';
    if (email.endsWith('@fibonaccipartner.com') || email.endsWith('@leoads.ai')) return true;
    const operatorEmail = storage.getItem<string>(LocalStorageKey.MASQUERADE_OPERATOR_EMAIL);
    if (!operatorEmail || !masqueradeUser) return false;
    return operatorEmail.endsWith('@fibonaccipartner.com') || operatorEmail.endsWith('@leoads.ai');
  }, [currentUser, masqueradeUser]);

  const hasRole = useCallback(
    (role: UserRole) => currentUser?.user_type === role,
    [currentUser],
  );

  const logout = useCallback(async () => {
    try {
      storage.clear();
      sessionStorage.removeItem('smart_landing_done');
      useAppStore.persist.clearStorage();
      setIsAuthenticated(false);
      setCurrentUser(null);
      setAccessTokenExpires(0);
      setMasqueradeUser(null);
      Sentry.setUser(null);
      queryClient.clear();
      await AuthService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      if (IS_ANONYMOUS_APP || demoMode) {
        window.location.href = LANDING_PAGE_URL;
      } else if (COGNITO_LOGOUT_URL) {
        window.location.href = getCognitoLogoutUrl();
      }
    }
  }, [demoMode]);

  // Enhanced user profile loading with branding
  const loadUserProfile = useCallback(
    async (
      masquadeUser: MasqueradeUser | null = null,
      isSilent: boolean = false,
    ) => {
    const isMasquerade = typeof masquadeUser !== 'undefined' && masquadeUser;
    setIsSilent(isSilent);
    setLoading(true);
    if (isMasquerade) {
      setIsMasquerading(true);
      // Read operator email from localStorage (not React state) to avoid stale closure —
      // currentUser is not in this useCallback's dependency array.
      const userProfile = storage.getItem<any>(LocalStorageKey.USER_PROFILE);
      const operatorEmail = userProfile?.user_name;
      if (operatorEmail) {
        storage.setItem(LocalStorageKey.MASQUERADE_OPERATOR_EMAIL, operatorEmail);
      }
    }

    try {
      const response = await AuthService.getCurrentUser();

      // CRITICAL: Check if workspace is changing (including fresh logins)
      // If a different user logs in without prior logout, Zustand stores may have
      // hydrated with the previous user's workspace key. A page reload clears all
      // in-memory state and forces stores to rehydrate with the correct workspace.
      // This aligns with main's pattern of using reload for workspace switches.
      const currentWorkspace = storage.getItem<string>(LocalStorageKey.WORKSPACE);
      const incomingWorkspace = response?.companyName;
      
      // Guard against infinite reload loops (e.g., if localStorage write fails)
      const RELOAD_KEY = 'workspace_reload_count';
      const MAX_RELOADS = 3;
      
      if (currentWorkspace && currentWorkspace !== incomingWorkspace) {
        const reloadCount = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
        
        if (reloadCount >= MAX_RELOADS) {
          console.error('[AuthContext] Max reload attempts reached, aborting reload loop:', {
            previous: currentWorkspace,
            incoming: incomingWorkspace,
            attempts: reloadCount,
          });
          sessionStorage.removeItem(RELOAD_KEY);
          // Continue without reload - user may need to manually refresh or re-login
        } else {
          console.log('[AuthContext] Workspace mismatch detected, reloading:', {
            previous: currentWorkspace,
            incoming: incomingWorkspace,
            attempt: reloadCount + 1,
          });
          
          sessionStorage.setItem(RELOAD_KEY, String(reloadCount + 1));
          
          // Update workspace BEFORE reload so stores hydrate with correct key
          storage.setItem(LocalStorageKey.WORKSPACE, incomingWorkspace);
          storage.setItem(LocalStorageKey.USER_PROFILE, response);
          
          // Reload clears all in-memory state - future-proof for any new stores
          window.location.reload();
          return;
        }
      } else {
        // Clear reload counter on successful load (no mismatch)
        sessionStorage.removeItem(RELOAD_KEY);
      }

      storage.setItem(LocalStorageKey.USER_PROFILE, response);
      storage.setItem(LocalStorageKey.WORKSPACE, response?.companyName);

      let masquadedUser = storage.getItem(
        LocalStorageKey.MASQUERADE_USER,
      ) as MasqueradeUser | null;

      if (isMasquerade) {
        masquadedUser = masquadeUser;
      }

      Sentry.setUser({
        id: response?.userId,
        username: response?.name,
      });
      setChatContext({ workspaceName: response?.companyName });

      if ('company_active' in response && !response?.company_active) {
        logout();
      }
      
      setCurrentUser(response);
      setIsAuthenticated(true);

      if (response?.userId && !isMasquerade) {
        posthog.identify(response.userId, {
          email: response.user_name,
          company_name: response.companyDisplayName,
          subscription_status: response.subscription?.status ?? null,
        }, {
          leo_account_timestamp: response.account_timestamp ?? response.timestamp ?? new Date().toISOString(),
        });
        aliasLandingSession();
        posthog.reloadFeatureFlags();
      }

      // Set masquerade user session
      storage.setItem(LocalStorageKey.MASQUERADE_USER, masquadedUser);
      setMasqueradeUser(masquadedUser);

      // Set company name after user is authenticated
      if (response?.companyName) {
        setCompanyName(response.companyName);
      }

      if (isMasquerade) {
        // Clear React Query cache when masquerading to prevent showing previous user's data
        queryClient.clear();
        // Clear all browser-style tabs when masquerading
        clearAllTabs();
        window.location.href = '/';
      }

      // Smart login landing: staff with exactly one workspace auto-switch
      // into it on login. Backend flags this via defaultWorkspaceId on /me.
      // Only fires once per session (tracked in sessionStorage) and never
      // when the user is already in a workspace context.
      const defaultWsId = (response as any)?.defaultWorkspaceId;
      const alreadyInWorkspace = Boolean((response as any)?.homeCompany);
      const smartLandingDone =
        sessionStorage.getItem('smart_landing_done') === 'true';
      if (
        defaultWsId &&
        !alreadyInWorkspace &&
        !isMasquerade &&
        !smartLandingDone
      ) {
        sessionStorage.setItem('smart_landing_done', 'true');
        try {
          const { workspace } = await WorkspaceService.switchWorkspace(
            defaultWsId,
          );
          storage.setItem(
            LocalStorageKey.WORKSPACE,
            workspace?.companyName || '',
          );
          window.location.reload();
          return;
        } catch (err) {
          console.warn(
            '[AuthContext] Smart login landing failed, staying at parent:',
            err,
          );
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      if (IS_ANONYMOUS_APP || demoMode) {
        // For demo/anonymous users, don't redirect to the landing page on error.
        // Clear auth state so the DemoIntake overlay re-appears in-place.
        storage.removeItem(LocalStorageKey.USER_PROFILE);
        setIsAuthenticated(false);
        setCurrentUser(null);
      } else {
        logout();
      }
    } finally {
      setLoading(false);
      setIsMasquerading(false);
    }
  }, [logout, setCompanyName, clearAllTabs, demoMode]);

  const setMasqueradeLogin = useCallback((user: MasqueradeUser | null) => {
    loadUserProfile(user);
  }, [loadUserProfile]);

  const updateOnboardStep = useCallback(
    (step: number) => {
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          onboard_completed_step: step,
        };
        setCurrentUser(updatedUser);
        storage.setItem(LocalStorageKey.USER_PROFILE, updatedUser);
      }
    },
    [currentUser],
  );

  const setUserlogin = useCallback(
    ({ profile }: SetUserLoginType = {}) => {
      try {
        if (profile) {
          setIsAuthenticated(true);
          setMasqueradeUser(null);
          Sentry.setUser({
            id: profile?.userId,
            username: profile?.name,
          });
          storage.setItem(LocalStorageKey.USER_PROFILE, profile);
          setCurrentUser(profile);
        } else {
          loadUserProfile();
        }
      } catch (error) {
        console.error('Error during login:', error);
      }
    },
    [loadUserProfile],
  );


  // Load user profile on initial mount if authenticated or returning from federated login
  useEffectOnce(() => {
    if (isAuthenticated) {
      !currentUser && loadUserProfile();
      return;
    }
    setLoading(false);
  }, [isAuthenticated]);

  // Handle federated login/signup callback
  useEffect(() => {
    // Backend redirects with federated_login_success=1 (home) or federated_signup_success=1 (signup);
    // we have no USER_PROFILE in localStorage yet, so load profile from cookie once.
    const params = new URLSearchParams(window.location.search);
    const isFederatedLogin = params.get('federated_login_success') === '1';
    const isFederatedSignup = params.get('federated_signup_success') === '1';
    if (isFederatedLogin || isFederatedSignup) {
      if (isFederatedLogin) {
        const fbq = (window as unknown as { fbq?: (action: string, name: string) => void }).fbq;
        if (typeof fbq === 'function') {
          fbq('track', 'CompleteRegistration');
        }
        // Clear demo mode — user just converted from anonymous via federated login
        setDemoMode(false);
      }
      // Load profile early so PostHog identify() + cross-domain alias run
      // before the user interacts with any forms.
      loadUserProfile()
        .then(() => {
          // Federated signup skips the email/password + verification steps.
          // Backfill those events (after identify) so the ordered funnel stays intact.
          if (isFederatedSignup) {
            posthog.capture(EVENTS.SIGNUP.ACCOUNT_CREATED, { method: 'federated' });
            posthog.capture(EVENTS.SIGNUP.EMAIL_VERIFIED, { method: 'federated' });
          }
        })
        .finally(() => {
          window.history.replaceState({}, '', window.location.pathname);
          setLoading(false);
        });
    }
  }, [isAuthenticated])

  // Check the access token expires from local storage
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const checkStorage = setInterval(() => {
      try {
        const expiresAt = storage.getItem<number>(LocalStorageKey.ACCESS_TOKEN_EXPIRES);
        if (expiresAt && expiresAt !== accessTokenExpires) {
          setAccessTokenExpires(expiresAt);
        }
      } catch (error) {
        console.warn('Failed to get access token expires from storage:', error);
      }
    }, 1000);
    return () => clearInterval(checkStorage);
  }, [isAuthenticated, accessTokenExpires]);

  // Coordinate refresh across tabs via BroadcastChannel to prevent refresh storms.
  // When one tab starts a refresh, others wait for the result instead of duplicating.
  const refreshChannelRef = useRef<BroadcastChannel | null>(null);
  const peerRefreshingRef = useRef(false);
  const peerRefreshStartedAtRef = useRef(0);

  useEffect(() => {
    try {
      const channel = new BroadcastChannel(REFRESH_CHANNEL_NAME);
      refreshChannelRef.current = channel;
      channel.onmessage = (event: MessageEvent<RefreshMessage>) => {
        if (event.data.tabId === TAB_ID) return;
        if (event.data.type === 'refresh_start') {
          peerRefreshingRef.current = true;
          peerRefreshStartedAtRef.current = event.data.timestamp || Date.now();
        } else if (event.data.type === 'refresh_done') {
          peerRefreshingRef.current = false;
          peerRefreshStartedAtRef.current = 0;
        }
      };
      return () => { channel.close(); refreshChannelRef.current = null; };
    } catch {
      // BroadcastChannel not supported (e.g. some WebViews) - no-op
    }
  }, []);

  const coordinatedRefresh = useCallback(async () => {
    if (
      peerRefreshingRef.current &&
      Date.now() - peerRefreshStartedAtRef.current > PEER_REFRESH_STALE_MS
    ) {
      peerRefreshingRef.current = false;
      peerRefreshStartedAtRef.current = 0;
    }

    if (!isAuthenticatedRef.current || isRefreshingRef.current || peerRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      refreshChannelRef.current?.postMessage({ type: 'refresh_start', tabId: TAB_ID, timestamp: Date.now() } satisfies RefreshMessage);
      await AuthService.refreshToken();
    } catch {
      // Refresh failure is handled by the ApiService interceptor
    } finally {
      isRefreshingRef.current = false;
      refreshChannelRef.current?.postMessage({ type: 'refresh_done', tabId: TAB_ID, timestamp: Date.now() } satisfies RefreshMessage);
    }
  }, []);

  // Set up a timeout for refreshing the access token
  useEffect(() => {
    if (!accessTokenExpires || !isAuthenticated) {
      return;
    }

    const timeToExpiration = (accessTokenExpires - Math.floor(Date.now() / 1000)) - 30;
    if (timeToExpiration <= 0) {
      coordinatedRefresh();
      return;
    }

    const timeout = setTimeout(coordinatedRefresh, timeToExpiration * 1000);
    return () => clearTimeout(timeout);
  }, [accessTokenExpires, isAuthenticated, coordinatedRefresh]);

  // Refresh token when returning to a backgrounded tab.
  // Chrome aggressively throttles setTimeout in background tabs, so the
  // proactive timer above may not fire while the tab is hidden. This
  // listener catches the case where the user returns after the access
  // token cookie has already expired.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticatedRef.current && !isRefreshingRef.current) {
        const expiresAt = storage.getItem<number>(LocalStorageKey.ACCESS_TOKEN_EXPIRES) || 0;
        const now = Math.floor(Date.now() / 1000);
        if (expiresAt - now < 60) {
          coordinatedRefresh();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [coordinatedRefresh]);

  // Loading check for authentication
  const isAppLoading = useMemo(() => {
    return loading && !isSilent;
  }, [loading, isSilent]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      accessTokenExpires,
      isAdmin,
      isAnonymous,
      setUserlogin,
      logout,
      currentUser,
      loadUserProfile,
      updateOnboardStep,
      hasRole,
      setMasqueradeLogin,
      masqueradeUser,
      isSuperAdmin,
      isMasquerading,
      isInternalUser,
      isFreeTrialExpired,
      isBrandingEnabled,
    }),
    [
      isAuthenticated,
      accessTokenExpires,
      isAdmin,
      isAnonymous,
      setUserlogin,
      logout,
      currentUser,
      loadUserProfile,
      updateOnboardStep,
      hasRole,
      setMasqueradeLogin,
      masqueradeUser,
      isSuperAdmin,
      isMasquerading,
      isInternalUser,
      isFreeTrialExpired,
      isBrandingEnabled,
    ],
  );

  // Always render AuthContext.Provider so useAuth never sees a missing provider when
  // children are mounted. Showing the app loader by omitting the provider caused
  // races (e.g. loadUserProfile() during onboarding) where descendants still ran useAuth.
  return (
    <AuthContext.Provider value={value}>
      {isAppLoading ? (
        <FullscreenLoader />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

// Enhanced useAuth hook with better error handling
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === null) {
    // More detailed error message
    const error = new Error(
      'useAuth must be used within an AuthProvider. ' +
      'Make sure your component is wrapped in <AuthProvider>. ' +
      'Check your component hierarchy and ensure AuthProvider is at the root level.',
    );

    // Add debugging info
    console.error('Auth Context Error Details:', {
      component: 'useAuth',
      error: 'Context is null',
      possibleCauses: [
        'Component is outside AuthProvider',
        'AuthProvider not properly imported',
        'Multiple React instances',
        'Component rendered during SSR without proper setup',
      ],
      stackTrace: new Error().stack,
    });

    throw error;
  }

  return context;
};

// Safe hook that doesn't throw errors (useful for error boundaries)
export const useSafeAuth = () => {
  try {
    const context = useContext(AuthContext);
    return context;
  } catch (error) {
    console.warn('useSafeAuth: Auth context not available:', error);
    return null;
  }
};

// Hook to check if auth is available
export const useAuthAvailable = () => {
  try {
    const context = useContext(AuthContext);
    return context !== null;
  } catch (error) {
    return false;
  }
};
